import { useState, useEffect, useCallback } from 'react';
import { makeAuthenticatedRequest } from '../../core/api/client';

if (process.env.NODE_ENV === 'development') {
  const t = process.env.REACT_APP_DEV_TOKEN;
  if (t && !localStorage.getItem('access_token')) localStorage.setItem('access_token', t);
}

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtInput = (d) => { if (!d) return ''; try { const x=new Date(d); const p=(n)=>String(n).padStart(2,'0'); return `${p(x.getDate())}-${p(x.getMonth()+1)}-${x.getFullYear()}`; } catch { return ''; } };
const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); } catch { return d; } };

const FILTERS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'week', label: 'Due this week' },
  { key: 'notdue', label: 'Not yet due' },
  { key: 'all', label: 'All' },
];

export default function PaymentRemindersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('overdue');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeAuthenticatedRequest('/api/v1/outstanding?only_overdue=false&sale_type=LOCAL');
      if (!res.ok) throw new Error('Could not load invoices');
      setRows(await res.json());
      setError('');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const d = (r) => (r.days_overdue === null || r.days_overdue === undefined ? null : Number(r.days_overdue));
  const overdue = rows.filter((r) => d(r) > 0);
  const week = rows.filter((r) => d(r) !== null && d(r) <= 0 && d(r) >= -7);
  const notdue = rows.filter((r) => d(r) === null || d(r) < -7);
  const sum = (l) => l.reduce((s, r) => s + Number(r.balance || 0), 0);
  const shown = filter === 'all' ? rows : filter === 'overdue' ? overdue : filter === 'week' ? week : notdue;

  const Stat = ({ label, value, count, tone }) => {
    const tones = {
      blue: 'border border-blue-100 bg-blue-50 text-gray-900',
      red: 'bg-red-50 text-red-700', amber: 'bg-amber-50 text-amber-700', green: 'bg-green-50 text-green-700',
    };
    return (
      <div className={`rounded-xl px-5 py-4 min-w-[160px] flex-1 ${tones[tone]}`}>
        <p className="text-sm opacity-80">{label}</p>
        <p className="text-2xl font-bold mt-0.5">{value}</p>
        <p className="text-xs opacity-60 mt-0.5">{count} invoices</p>
      </div>
    );
  };

  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const uploadSheet = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setUploading(true); setUploadMsg('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await makeAuthenticatedRequest('/api/v1/payment-reminders/upload-sheet', { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setUploadMsg(`Loaded ${data.invoices_loaded} invoices${data.customers_unmatched ? `, ${data.customers_unmatched} customer names not matched` : ''}.`);
      load();
    } catch (err) { setUploadMsg(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Payment reminders</h1>
          <p className="text-sm text-gray-500 mt-1">Upload the weekly outstanding sheet, then review &amp; send. (Local customers)</p>
        </div>
        <div className="w-80 shrink-0">
          <label className="block border-[1.5px] border-dashed border-indigo-200 bg-indigo-50/40 rounded-xl px-5 py-4 text-center cursor-pointer hover:bg-indigo-50">
            <div className="flex items-center justify-center gap-2">
              <span className="text-indigo-600 text-lg">⬆</span>
              <span className="text-sm font-semibold text-indigo-600">{uploading ? 'Uploading…' : 'Upload outstanding sheet'}</span>
            </div>
            <div className="text-xs text-gray-400 mt-1">Drop .xlsx / .pdf here, or click to choose</div>
            <input type="file" accept=".xlsx,.xls,.pdf" onChange={uploadSheet} disabled={uploading} className="hidden" />
          </label>
          {uploadMsg && <p className="text-xs text-blue-700 mt-2 text-right">{uploadMsg}</p>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Stat label="Total due" value={fmt(sum(rows))} count={rows.length} tone="blue" />
        <Stat label="Overdue" value={fmt(sum(overdue))} count={overdue.length} tone="red" />
        <Stat label="Due this week" value={fmt(sum(week))} count={week.length} tone="amber" />
        <Stat label="Not yet due" value={fmt(sum(notdue))} count={notdue.length} tone="green" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 text-sm rounded-full border ${filter === f.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">{error === 'No authentication token found' ? 'Local view not signed in — add REACT_APP_DEV_TOKEN in frontend/.env.local and restart the frontend.' : error}</div>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-14 text-center text-sm text-gray-400">Nothing in this list.</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Customer</th>
                <th className="px-4 py-3 text-left font-medium">Invoice</th>
                <th className="px-4 py-3 text-left font-medium">Due</th>
                <th className="px-4 py-3 text-right font-medium">Balance</th>
                <th className="px-4 py-3 text-right font-medium">Overdue</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900">{r.customer}</td>
                  <td className="px-4 py-3 text-gray-700">{r.invoice_no}<div className="text-xs text-gray-400">{fmtDate(r.invoice_date)}</div></td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(r.balance)}</td>
                  <td className={`px-4 py-3 text-right font-medium ${d(r) > 0 ? 'text-red-600' : 'text-gray-400'}`}>{d(r) > 0 ? `${d(r)} d` : d(r) === null ? '—' : 'not due'}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setSelected({ ...r, mode: 'view' })} className="text-sm text-blue-600 mr-3">View</button>
                    <button onClick={() => setSelected({ ...r, mode: 'review' })} className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700">Review &amp; send</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-gray-400">View = open the uploaded invoice · Review &amp; send = check the message, then send email + WhatsApp.</p>

      {selected && <ReviewModal row={selected} onClose={() => setSelected(null)} onSent={load} />}
    </div>
  );
}

function ReviewModal({ row, onClose, onSent }) {
  const dm = `Dear Sir,\n\nThis is a gentle reminder that payment for the following invoice is due:\n\nInvoice No: ${row.invoice_no}\nDate: ${fmtDate(row.invoice_date)}\nAmount: ₹${Number(row.balance || 0).toLocaleString('en-IN')}\nDue on: ${fmtDate(row.due_date)}\n\nWe would appreciate it if you could arrange the payment at your earliest convenience.\n\nRegards,\nHarshada Pawar\nCustomer Relationship Management\nFor Alok Ingots (Mumbai) Pvt. Ltd.`;
  const [msg, setMsg] = useState(dm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState('');
  const [useEmail, setUseEmail] = useState(true);
  const [useWhatsapp, setUseWhatsapp] = useState(false);

  const send = async () => {
    if (!useEmail && !useWhatsapp) { setDone('Tick Email and/or WhatsApp first.'); return; }
    setSending(true);
    const results = [];
    try {
      if (useEmail) {
        const res = await makeAuthenticatedRequest(`/api/v1/payment-reminders/${row.id}/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: msg, to_email: row.contact_email }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Could not send email'); }
        results.push('Email sent ✓');
      }
      if (useWhatsapp) {
        const rawPhone = (row.contact_phone || '').replace(/[^0-9]/g, '');
        if (!rawPhone) {
          results.push('WhatsApp skipped (no phone)');
        } else {
          const phone = rawPhone.length === 10 ? '91' + rawPhone : rawPhone;
          const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
          window.open(waUrl, '_blank');
          results.push('WhatsApp opened ✓ (tap send)');
        }
      }
      setDone(results.join(' · '));
      onSent && onSent();
      if (useEmail) setTimeout(onClose, 1400);
    } catch (e) { setDone(e.message); } finally { setSending(false); }
  };

  const [edit, setEdit] = useState({
    invoice_no: row.invoice_no || '',
    invoice_date: fmtInput(row.invoice_date),
    due_date: fmtInput(row.due_date),
    balance: row.balance ?? '',
  });
  const [saving, setSaving] = useState(false);
  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/payment-reminders/${row.id}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_no: edit.invoice_no,
          invoice_date: edit.invoice_date,
          due_date: edit.due_date,
          balance: edit.balance === '' ? null : Number(edit.balance),
        }),
      });
      if (!res.ok) throw new Error('Could not save');
      setDone('Saved ✓'); onSent && onSent(); setTimeout(onClose, 800);
    } catch (e) { setDone(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><h2 className="text-lg font-semibold text-gray-900">Review &amp; send</h2>
          <p className="text-sm text-gray-500">Check everything, then send to the customer.</p></div>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-sm">
          <div className="font-medium text-gray-900">{row.customer}</div>
          <div className="text-gray-500 text-xs mt-1">{row.contact_email || 'no email'} · {row.contact_phone || 'no phone'}</div>
          <div className="text-gray-500 text-xs mt-2">Invoice {row.invoice_no} · ₹{Number(row.balance || 0).toLocaleString('en-IN')} · due {fmtDate(row.due_date)}</div>
        </div>
        {row.mode === 'view' ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Invoice number</label>
                <input value={edit.invoice_no} onChange={(e)=>setEdit({...edit, invoice_no:e.target.value})} className="w-full mt-1 rounded-lg border border-gray-300 p-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500">Balance (₹)</label>
                <input value={edit.balance} onChange={(e)=>setEdit({...edit, balance:e.target.value})} className="w-full mt-1 rounded-lg border border-gray-300 p-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500">Invoice date (dd-mm-yyyy)</label>
                <input value={edit.invoice_date} onChange={(e)=>setEdit({...edit, invoice_date:e.target.value})} placeholder="dd-mm-yyyy" className="w-full mt-1 rounded-lg border border-gray-300 p-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500">Due date (dd-mm-yyyy)</label>
                <input value={edit.due_date} onChange={(e)=>setEdit({...edit, due_date:e.target.value})} placeholder="dd-mm-yyyy" className="w-full mt-1 rounded-lg border border-gray-300 p-2 text-sm" /></div>
            </div>
            <div className="text-xs text-gray-400">Read by OCR — fix anything wrong, then save.</div>
            {done && <div className="text-sm text-green-700">{done}</div>}
            <div className="flex gap-2">
              <button onClick={saveEdit} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-400 hover:bg-blue-700">{saving ? 'Saving…' : 'Save changes'}</button>
              <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div><label className="text-xs text-gray-500">Message</label>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={6} className="w-full mt-1 rounded-lg border border-gray-300 p-2 text-sm" /></div>
            <div className="flex items-center gap-5">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={useEmail} onChange={(e)=>setUseEmail(e.target.checked)} /> Email
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={useWhatsapp} onChange={(e)=>setUseWhatsapp(e.target.checked)} /> WhatsApp
              </label>
            </div>
            {done && <div className="text-sm text-green-700">{done}</div>}
            <div className="flex gap-2">
              <button onClick={send} disabled={sending} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-400 hover:bg-blue-700">{sending ? 'Sending…' : (useEmail && useWhatsapp ? 'Send email + WhatsApp' : useWhatsapp ? 'Send WhatsApp' : 'Send email')}</button>
              <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
